import React from 'react';
import { motion } from 'framer-motion';
import { Brain, Workflow, FileText, Database, Zap, Shield, MessageSquare, Globe, Monitor } from 'lucide-react';

const AI_GENES = [
  {
    name: "PulseGene",
    tagline: "The Thinking Engine",
    description: "The crown jewel of our AI ecosystem. PulseGene doesn't just process data—it understands context, learns from every interaction, and orchestrates intelligent workflows across your entire system landscape.",
    capabilities: [
      "Processes 1000+ documents per minute",
      "97% accuracy in data validation",
      "Learns and adapts in real-time",
      "Integrates with any legacy system"
    ],
    icon: Brain,
    color: "from-blue-500 to-cyan-500"
  },
  {
    name: "RouteGene",
    tagline: "Intelligent Task Orchestration",
    description: "Transform chaotic project management into streamlined execution. RouteGene intelligently assigns tasks, predicts bottlenecks, and orchestrates complex workflows across teams and systems with unprecedented efficiency and insight.",
    capabilities: [
      "Smart task routing & prioritization",
      "Predictive project planning",
      "Cross-team workflow coordination",
      "Intelligent resource optimization"
    ],
    icon: Workflow,
    color: "from-emerald-500 to-teal-500"
  },
  {
    name: "ScribeGene",
    tagline: "AI-Powered Document Intelligence",
    description: "Transform chaotic multi-document PDFs into organized, classified intelligence. ScribeGene uses AI to automatically identify, split, and process different document types—from policy illustrations to correspondence—creating structured data from unstructured documents.",
    capabilities: [
      "AI document type classification",
      "Intelligent PDF splitting & extraction",
      "Multi-format content processing",
      "Google Cloud Vision & Vertex AI integration"
    ],
    icon: FileText,
    color: "from-purple-500 to-pink-500"
  },
  {
    name: "ClouGene",
    tagline: "Intelligent Cloud Storage & Orchestration",
    description: "Your enterprise-grade cloud storage platform that thinks ahead. ClouGene securely manages your files, automatically organizes incoming documents, schedules critical tasks, and orchestrates your digital inbox—ensuring nothing falls through the cracks while maintaining complete security and compliance.",
    capabilities: [
      "Secure cloud file storage & sharing",
      "Automated document organization",
      "Smart inbox orchestration",
      "Intelligent task scheduling"
    ],
    icon: Database,
    color: "from-orange-500 to-red-500"
  },
  {
    name: "DocuGene",
    tagline: "Document Intelligence Engine",
    description: "Transform your document workflows with intelligent template management and generation. DocuGene creates, manages, and generates customized documents with API-first design and real-time preview capabilities.",
    capabilities: [
      "Dynamic document generation",
      "Template management system",
      "API-first architecture",
      "Real-time document preview"
    ],
    icon: Zap,
    color: "from-yellow-500 to-orange-500"
  },
  {
    name: "CalcGene",
    tagline: "Excel-to-API Transformation Engine",
    description: "End the chaos of multiple Excel versions floating around your organization. CalcGene transforms your critical spreadsheets into secure, governed APIs that prevent unauthorized changes while maintaining all your complex formulas and business logic.",
    capabilities: [
      "Excel-to-API transformation",
      "Version control & governance",
      "Unauthorized change prevention",
      "Complex formula preservation"
    ],
    icon: Shield,
    color: "from-green-500 to-emerald-500"
  },
  {
    name: "ConvoGene",
    tagline: "RAG-Powered Conversation Intelligence",
    description: "Your intelligent conversation partner that knows everything about your organization. ConvoGene uses RAG technology to provide contextual responses, can orchestrate PulseGene workflows, and connects to all AI genes to execute complex multi-step processes through natural conversation.",
    capabilities: [
      "RAG-powered contextual responses",
      "PulseGene orchestration via chat",
      "Cross-gene workflow execution",
      "Natural language task management"
    ],
    icon: MessageSquare,
    color: "from-indigo-500 to-purple-500"
  },
  {
    name: "BrowGene",
    tagline: "Web Intelligence Automation",
    description: "Turn every web application into an extension of your team. BrowGene navigates complex online interfaces, automates repetitive web tasks, and integrates disparate web-based systems with human-like intelligence and precision.",
    capabilities: [
      "Intelligent web process automation",
      "Cross-platform integration",
      "Adaptive interface navigation",
      "Real-time web task orchestration"
    ],
    icon: Globe,
    color: "from-cyan-500 to-blue-500"
  },
  {
    name: "DeskGene",
    tagline: "Visual Desktop Intelligence",
    description: "Bring AI intelligence to any desktop application or legacy system. DeskGene sees what humans see, understands visual interfaces, and executes complex desktop workflows with the precision of your best employees—but never gets tired.",
    capabilities: [
      "Visual interface automation",
      "Legacy system integration",
      "Intelligent screen interaction",
      "Desktop workflow optimization"
    ],
    icon: Monitor,
    color: "from-rose-500 to-pink-500"
  }
];

const AIGenesSection: React.FC = () => {
  return (
    <section id="ai-genes" className="py-20 bg-gradient-to-b from-gray-900 to-black">
      <div className="container mx-auto px-4">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-400 via-purple-400 to-emerald-400">
              Meet the AI Genes
            </span>
          </h2>
          <p className="text-lg text-gray-300 max-w-3xl mx-auto leading-relaxed">
            Each AI Gene is a specialized intelligence, engineered to master a specific aspect of insurance operations. 
            Together, they form an ecosystem that doesn't just automate—it <span className="text-blue-400 font-semibold">evolves</span>.
          </p>
        </motion.div>

        {/* AI Genes Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {AI_GENES.map((gene, index) => {
            const IconComponent = gene.icon;
            return (
              <motion.div
                key={gene.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="relative group"
              >
                <div className="bg-gray-800/50 backdrop-blur-sm border border-gray-700/50 rounded-xl p-5 h-full hover:border-gray-600/50 transition-all duration-300 group-hover:transform group-hover:scale-105">
                  {/* Icon and Header */}
                  <div className="flex items-center mb-3">
                    <div className={`p-2 rounded-lg bg-gradient-to-r ${gene.color} mr-3`}>
                      <IconComponent className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-white">{gene.name}</h3>
                      <p className={`text-xs font-medium bg-gradient-to-r ${gene.color} bg-clip-text text-transparent`}>
                        {gene.tagline}
                      </p>
                    </div>
                  </div>

                  {/* Description */}
                  <p className="text-sm text-gray-300 mb-3 leading-relaxed">
                    {gene.description}
                  </p>

                  {/* Capabilities */}
                  <div className="space-y-1.5">
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Key Capabilities</p>
                    <ul className="space-y-1">
                      {gene.capabilities.map((capability, capIndex) => (
                        <li key={capIndex} className="text-xs text-gray-300 flex items-center">
                          <div className={`w-1 h-1 rounded-full bg-gradient-to-r ${gene.color} mr-2`}></div>
                          {capability}
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Hover Effect */}
                  <div className={`absolute inset-0 bg-gradient-to-r ${gene.color} opacity-0 group-hover:opacity-5 rounded-xl transition-opacity duration-300`}></div>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Call to Action */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.8 }}
          className="text-center mt-16"
        >
          <div className="bg-gradient-to-r from-blue-900/30 to-purple-900/30 rounded-2xl p-8 border border-blue-500/20">
            <h3 className="text-3xl font-bold text-white mb-4">
              Ready to See Your AI Genes in Action?
            </h3>
            <p className="text-xl text-gray-300 mb-6 max-w-2xl mx-auto">
              Every insurance company is unique. That's why we customize your AI Gene combination 
              to match your specific needs, challenges, and opportunities.
            </p>
            <motion.a
              href="#contact"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-full text-lg font-semibold bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white transition-all duration-300 shadow-[0_0_20px_rgba(59,130,246,0.3)] hover:shadow-[0_0_25px_rgba(59,130,246,0.4)]"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Discover Your Perfect AI Gene Combination
            </motion.a>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default AIGenesSection;
